import React, { useState, useEffect, useRef } from 'react'
import { supabase } from '../../lib/supabase'
import { fmtMoney } from '../../utils/formatters'
import {
  DEFAULT_SCOPE_ITEMS, DEFAULT_EQUIPMENT_ITEMS, STANDARD_NOT_INCLUDED, DEFAULT_COPING_OPTIONS, NON_DIVING_NOTE,
  DEFAULT_MATERIALS_DEPOSIT, DEFAULT_RETAINER_PCT, paymentSchedule, sortOptions,
  listToText, textToList, specsToText, textToSpecs,
} from '../../utils/proposal'

// CP Quality Pools proposal fields. Lists are edited as one item per line.
// onUpdate: debounced bid save; onUpdateNow: immediate bid save;
// options / setOptions: the bid's bid_options rows (coping choices).
export default function ProposalEditor({ bid, onUpdate, onUpdateNow, options, setOptions }) {
  var isProposal = bid.quote_format === 'proposal'
  var sched = paymentSchedule(bid)

  var loadDefaults = async function() {
    var updates = {}
    if (!(bid.scope_items || []).length) updates.scope_items = DEFAULT_SCOPE_ITEMS.slice()
    if (!(bid.equipment_items || []).length) updates.equipment_items = DEFAULT_EQUIPMENT_ITEMS.slice()
    if (!(bid.not_included || []).length) updates.not_included = STANDARD_NOT_INCLUDED.slice()
    if (!bid.pool_specs_note) updates.pool_specs_note = NON_DIVING_NOTE
    if (Object.keys(updates).length) await onUpdateNow(updates)
    if (!options.length) {
      var rows = DEFAULT_COPING_OPTIONS.map(function(o, i) { return { ...o, bid_id: bid.id, option_group: 'coping', sort_order: i } })
      var { data } = await supabase.from('bid_options').insert(rows).select()
      if (data) setOptions(data)
    }
  }

  return (
    <div style={s.wrap}>
      <div style={s.headRow}>
        <div style={s.title}>PROPOSAL</div>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {[['standard', 'STANDARD LAYOUT'], ['proposal', 'INGROUND PROPOSAL']].map(function(f) {
            var active = (bid.quote_format || 'standard') === f[0]
            return <button key={f[0]} onClick={function() { onUpdateNow({ quote_format: f[0] }) }} style={{ ...s.toggle, ...(active ? s.toggleActive : {}) }}>{f[1]}</button>
          })}
          {isProposal && <button onClick={loadDefaults} style={s.smallBtn}>FILL EMPTY WITH DEFAULTS</button>}
        </div>
      </div>

      <div style={s.grid3}>
        <div>
          <label style={s.label}>DATE ISSUED</label>
          <input type="date" value={bid.issued_date || ''} onChange={function(e) { onUpdateNow({ issued_date: e.target.value || null }) }} style={s.input} />
          <div style={s.hint}>Set automatically the first time the quote is downloaded or sent.</div>
        </div>
        <NumField label="WATER MITIGATION CHARGE ($)" value={bid.water_mitigation_amount} placeholder="None" onSave={function(v) { onUpdate({ water_mitigation_amount: v }) }} hint="Leave blank to leave the clause off." />
      </div>

      {isProposal && (
        <>
          <div style={s.grid2}>
            <TextList key={bid.id + 'specs'} label="POOL SPECIFICATIONS (Label: value, one per line)" text={specsToText(bid.pool_specs)} rows={8} onSave={function(t) { onUpdate({ pool_specs: textToSpecs(t) }) }} />
            <div>
              <TextList key={bid.id + 'note'} label="SPECIFICATIONS NOTE" text={bid.pool_specs_note || ''} rows={3} onSave={function(t) { onUpdate({ pool_specs_note: t.trim() || null }) }} />
              <div style={{ height: '12px' }} />
              <TextList key={bid.id + 'equip'} label="EQUIPMENT PACKAGE (one per line)" text={listToText(bid.equipment_items)} rows={5} onSave={function(t) { onUpdate({ equipment_items: textToList(t) }) }} />
            </div>
          </div>
          <TextList key={bid.id + 'scope'} label="SCOPE OF WORK (one per line)" text={listToText(bid.scope_items)} rows={8} onSave={function(t) { onUpdate({ scope_items: textToList(t) }) }} />
        </>
      )}

      <div style={{ height: '12px' }} />
      <TextList key={bid.id + 'notincl'} label="NOT INCLUDED (one per line)" text={listToText(bid.not_included)} rows={5} onSave={function(t) { onUpdate({ not_included: textToList(t) }) }} />

      {isProposal && (
        <>
          <CopingOptions bid={bid} options={options} setOptions={setOptions} />

          <div style={s.subTitle}>PAYMENT SCHEDULE</div>
          <div style={s.grid3}>
            <NumField label="POOL KIT COST (DUE AT ORDER)" value={bid.kit_deposit_amount} placeholder="Required" onSave={function(v) { onUpdate({ kit_deposit_amount: v }) }} />
            <NumField label="MATERIALS DEPOSIT (DUE AT INSTALL)" value={bid.materials_deposit_amount} placeholder={String(DEFAULT_MATERIALS_DEPOSIT)} onSave={function(v) { onUpdate({ materials_deposit_amount: v }) }} />
            <NumField label="RETAINER %" value={bid.retainer_pct} placeholder={String(DEFAULT_RETAINER_PCT)} onSave={function(v) { onUpdate({ retainer_pct: v }) }} />
          </div>
          <div style={s.schedule}>
            {sched.rows.map(function(r, i) {
              return <div key={r.key} style={s.schedRow}><span style={{ flex: 2, color: '#fff' }}>{i + 1}. {r.milestone}</span><span style={{ flex: 3, color: 'rgba(255,255,255,0.4)' }}>{r.due}</span><span style={{ flex: 1, textAlign: 'right', color: r.amount < 0 ? '#e05c5c' : '#fff', fontWeight: 600 }}>{fmtMoney(r.amount)}</span></div>
            })}
            <div style={{ ...s.schedRow, borderBottom: 'none' }}><span style={{ flex: 5, color: '#b48c3c', fontWeight: 700 }}>CONTRACT PRICE</span><span style={{ flex: 1, textAlign: 'right', color: '#b48c3c', fontWeight: 700 }}>{fmtMoney(sched.total)}</span></div>
            {sched.warnings.map(function(w) { return <div key={w} style={s.warn}>{w}</div> })}
          </div>
        </>
      )}
    </div>
  )
}

function CopingOptions({ bid, options, setOptions }) {
  var sorted = sortOptions(options)
  var add = async function() {
    var { data } = await supabase.from('bid_options').insert({
      bid_id: bid.id, option_group: 'coping', label: '', price_adjustment: 0, is_base: options.length === 0, sort_order: options.length,
    }).select().single()
    if (data) setOptions(options.concat([data]))
  }
  var save = async function(id, updates) {
    await supabase.from('bid_options').update(updates).eq('id', id)
    setOptions(options.map(function(o) { return o.id === id ? { ...o, ...updates } : o }))
  }
  var makeBase = async function(id) {
    await supabase.from('bid_options').update({ is_base: false }).eq('bid_id', bid.id)
    await supabase.from('bid_options').update({ is_base: true }).eq('id', id)
    setOptions(options.map(function(o) { return { ...o, is_base: o.id === id } }))
  }
  var remove = async function(id) {
    await supabase.from('bid_options').delete().eq('id', id)
    setOptions(options.filter(function(o) { return o.id !== id }))
  }
  var base = parseFloat(bid.total) || 0
  return (
    <div>
      <div style={{ ...s.headRow, marginTop: '16px' }}>
        <div style={s.subTitle}>COPING OPTIONS</div>
        <button onClick={add} style={s.smallBtn}>+ ADD OPTION</button>
      </div>
      {sorted.length === 0 && <div style={s.hint}>No coping options — the section is left off the proposal.</div>}
      {sorted.map(function(o) {
        return <OptionRow key={o.id} option={o} base={base} onSave={function(u) { save(o.id, u) }} onBase={function() { makeBase(o.id) }} onDelete={function() { remove(o.id) }} />
      })}
    </div>
  )
}

function OptionRow({ option, base, onSave, onBase, onDelete }) {
  var [label, setLabel] = useState(option.label || '')
  var [adj, setAdj] = useState(option.price_adjustment != null ? String(option.price_adjustment) : '0')
  return (
    <div style={s.optRow}>
      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', width: '70px' }}>
        <input type="radio" checked={!!option.is_base} onChange={onBase} style={{ accentColor: '#b48c3c' }} />
        <span style={{ ...s.label, marginBottom: 0 }}>BASE</span>
      </label>
      <input value={label} onChange={function(e) { setLabel(e.target.value) }} onBlur={function() { if (label !== option.label) onSave({ label: label }) }} style={{ ...s.input, flex: 3 }} placeholder="e.g. Paver coping" />
      <input type="number" value={adj} onChange={function(e) { setAdj(e.target.value) }} onBlur={function() { var v = parseFloat(adj) || 0; if (v !== parseFloat(option.price_adjustment)) onSave({ price_adjustment: v }) }} style={{ ...s.input, flex: 1, textAlign: 'right' }} placeholder="+$" step="0.01" />
      <span style={{ flex: 1, textAlign: 'right', color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>{fmtMoney(base + (parseFloat(adj) || 0))}</span>
      <button onClick={onDelete} style={s.delBtn}>X</button>
    </div>
  )
}

// Local state so parsing on save never fights the cursor; picks up outside
// changes (calculator, defaults) whenever the field isn't being edited.
function useSyncedLocal(external) {
  var ref = useRef(null)
  var [val, setVal] = useState(external)
  useEffect(function() { if (document.activeElement !== ref.current) setVal(external) }, [external])
  return [val, setVal, ref]
}

function TextList({ label, text, rows, onSave }) {
  var [val, setVal, ref] = useSyncedLocal(text)
  return (
    <div>
      <label style={s.label}>{label}</label>
      <textarea ref={ref} value={val} rows={rows} onChange={function(e) { setVal(e.target.value); onSave(e.target.value) }} style={{ ...s.input, resize: 'vertical', lineHeight: '1.5', fontSize: '13px' }} />
    </div>
  )
}

function NumField({ label, value, placeholder, onSave, hint }) {
  var [val, setVal, ref] = useSyncedLocal(value != null ? String(value) : '')
  return (
    <div>
      <label style={s.label}>{label}</label>
      <input ref={ref} type="number" value={val} onChange={function(e) { setVal(e.target.value); onSave(e.target.value === '' ? null : parseFloat(e.target.value)) }} style={s.input} placeholder={placeholder} step="0.01" />
      {hint && <div style={s.hint}>{hint}</div>}
    </div>
  )
}

var s = {
  wrap: { padding: '20px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(180,140,60,0.2)', borderRadius: '4px', marginBottom: '24px' },
  headRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' },
  title: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '14px', fontWeight: 600, letterSpacing: '2px', color: '#b48c3c' },
  subTitle: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', fontWeight: 600, letterSpacing: '2px', color: '#b48c3c', margin: '16px 0 10px' },
  toggle: { padding: '6px 14px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '1.5px', cursor: 'pointer' },
  toggleActive: { backgroundColor: 'rgba(180,140,60,0.15)', borderColor: 'rgba(180,140,60,0.4)', color: '#b48c3c' },
  smallBtn: { padding: '6px 14px', backgroundColor: 'rgba(180,140,60,0.15)', border: '1px solid rgba(180,140,60,0.3)', borderRadius: '3px', color: '#b48c3c', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', fontWeight: 600, letterSpacing: '1.5px', cursor: 'pointer' },
  grid2: { display: 'grid', gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 1fr', gap: '12px', marginBottom: '12px' },
  grid3: { display: 'grid', gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' },
  label: { display: 'block', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', fontWeight: 600, letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: '#ffffff', fontSize: '14px', fontFamily: "'Barlow',sans-serif", outline: 'none', boxSizing: 'border-box' },
  hint: { fontSize: '11px', color: 'rgba(255,255,255,0.3)', marginTop: '4px' },
  optRow: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' },
  delBtn: { padding: '4px 8px', backgroundColor: 'rgba(224,92,92,0.1)', border: '1px solid rgba(224,92,92,0.2)', borderRadius: '3px', color: '#e05c5c', fontSize: '12px', cursor: 'pointer' },
  schedule: { border: '1px solid rgba(255,255,255,0.06)', borderRadius: '3px', padding: '4px 12px' },
  schedRow: { display: 'flex', gap: '10px', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)', fontSize: '13px' },
  warn: { color: '#e05c5c', fontSize: '12px', padding: '6px 0' },
}
